import { FormEvent, useEffect, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Textarea } from "@/components/ui/Textarea";
import { useApiMutation } from "@/lib/api";
import { useToast } from "@/hooks/useToast";
import { MeasurementRow } from "@/types/measurements";

interface Props {
  open: boolean;
  periodId: string;
  row: MeasurementRow | null;
  rows: MeasurementRow[];
  onClose: () => void;
  onSaved: (logLabel?: string) => void;
}

const USAGE_UNIT: Record<string, string> = {
  "zimna-woda": "m³",
  "podgrzanie-cw": "m³",
  "co": "GJ",
  "en-el": "kWh",
  "gaz": "m³",
};

function isUpdateFor(r: MeasurementRow | null): boolean {
  return !!r && !r.missing && r.value !== null;
}

export function EditMeasurementModal({ open, periodId, row, rows, onClose, onSaved }: Props) {
  const [value, setValue] = useState("");
  const [usage, setUsage] = useState("");
  const [amount, setAmount] = useState("");
  const [coldValue, setColdValue] = useState("");
  const [hotValue, setHotValue] = useState("");
  const [notes, setNotes] = useState("");
  const { mutate, loading } = useApiMutation();
  const { push } = useToast();

  // A location has a separate hot-water meter (podgrzanie-cw) only when
  // configured (LOCATION_CONFIG.hot_water_metered) — krasinskiego doesn't,
  // since its heating isn't administrator-metered. When it does, the cold
  // meter reads cold+hot combined, so both cells are corrected in one save.
  const hotRow = row ? rows.find((r) => r.location === row.location && r.utility === "podgrzanie-cw") ?? null : null;
  const coldRow = row ? rows.find((r) => r.location === row.location && r.utility === "zimna-woda") ?? null : null;
  const dualMode = !!row && row.editable && (row.utility === "zimna-woda" || row.utility === "podgrzanie-cw") && !!hotRow;

  // Reset on row change.
  useEffect(() => {
    if (open && row) {
      setValue(row.value ?? "");
      setUsage(row.usage ?? "");
      setAmount(row.amount ?? "");
      // The combined total from a prior save can't be un-mixed into its cold
      // and hot components, so both start blank even when correcting an
      // existing entry — the same as re-reading both meters by hand.
      setColdValue("");
      setHotValue("");
      setNotes("");
    }
  }, [open, row]);

  if (!row) return null;
  const [year, month] = periodId.split("-").map(Number);

  const coldNum = parseFloat(coldValue);
  const hotNum = parseFloat(hotValue);
  const dualValid = !Number.isNaN(coldNum) && !Number.isNaN(hotNum);
  const coldOut = dualValid ? (coldNum + hotNum).toFixed(2) : "";
  const hotOut = dualValid ? hotNum.toFixed(2) : "";

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!row) return;
    try {
      if (dualMode) {
        if (!dualValid) return;
        await mutate("/api/utility/reading", isUpdateFor(coldRow) ? "PUT" : "POST", {
          year, month, location: row.location, utility: "zimna-woda", value: coldOut, notes,
        });
        await mutate("/api/utility/reading", isUpdateFor(hotRow) ? "PUT" : "POST", {
          year, month, location: row.location, utility: "podgrzanie-cw", value: hotOut, notes,
        });
        push({ message: `Zapisano zimną i ciepłą wodę (${row.location})`, tone: "success" });
        onSaved(`Zapisano odczyt: zimna + ciepła woda (${row.location})`);
        onClose();
        return;
      }
      if (row.editable) {
        // Meter row: writes the raw meter reading to flatMngt-utility-readings.
        const isUpdate = isUpdateFor(row);
        await mutate("/api/utility/reading", isUpdate ? "PUT" : "POST", {
          year, month,
          location: row.location,
          utility: row.utility,
          value,
          notes,
        });
        onSaved(`Zapisano odczyt: ${row.location} / ${row.utility}`);
      } else {
        // Non-meter row: writes (amount, usage) to flatMngt-balance,
        // overriding any scraped / auto value.
        await mutate("/api/billing/collection/entry", "POST", {
          year, month,
          location: row.location,
          utility: row.utility,
          value: amount,
          usage: usage || undefined,
        });
        onSaved(`Zapisano ręcznie: ${row.location} / ${row.utility}`);
      }
      push({ message: `Zapisano ${row.utility} (${row.location})`, tone: "success" });
      onClose();
    } catch (err) {
      push({ message: (err as Error).message, tone: "danger" });
    }
  }

  const usageUnit = USAGE_UNIT[row.utility] ?? "";
  const canSubmit = dualMode ? dualValid : (row.editable ? !!value : !!amount);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={dualMode ? `${row.location} — zimna + ciepła woda` : `${row.location} / ${row.utility}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={loading}>Anuluj</Button>
          <Button onClick={submit} loading={loading} disabled={!canSubmit}>
            Zapisz
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className="flex flex-col gap-3">
        {dualMode ? (
          <>
            <Input id="m-cold" label="Zimna — stan licznika (m³)" inputMode="decimal" autoFocus
                   value={coldValue} onChange={(e) => setColdValue(e.target.value)} />
            <Input id="m-hot" label="Ciepła — stan licznika (m³)" inputMode="decimal"
                   value={hotValue} onChange={(e) => setHotValue(e.target.value)} />
            <Input id="m-cold-out" label="Zimna woda — do zapisania (m³)" value={coldOut} disabled />
            <Input id="m-hot-out" label="Podgrzanie CW — do zapisania (m³)" value={hotOut} disabled />
            <Textarea id="m-notes" label="Notatki (opcjonalne)" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </>
        ) : row.editable ? (
          <>
            <Input id="m-value" label={`Stan licznika (${row.unit})`} inputMode="decimal" autoFocus
                   value={value} onChange={(e) => setValue(e.target.value)} />
            <Textarea id="m-notes" label="Notatki (opcjonalne)" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </>
        ) : (
          <>
            <Input id="m-amount" label="Kwota (PLN)" inputMode="decimal" autoFocus
                   value={amount} onChange={(e) => setAmount(e.target.value)} />
            <Input id="m-usage" label={`Zużycie${usageUnit ? ` (${usageUnit})` : ""} — opcjonalnie`}
                   inputMode="decimal"
                   value={usage} onChange={(e) => setUsage(e.target.value)} />
            <p className="text-xs text-text-muted">
              Zapis nadpisze wartości pobrane automatycznie / przez scraper.
            </p>
          </>
        )}
      </form>
    </Modal>
  );
}
